
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { VIEW_PERMISSIONS, NAV_TO_PATH, hasPermission } from "../page";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { icon: "ri-dashboard-line",   label: "Dashboard",  id: "dashboard" },
      { icon: "ri-bar-chart-2-line", label: "Analytics",  id: "analytics" },
    ],
  },
  {
    label: "Operations",
    items: [
      { icon: "ri-exchange-line",           label: "Transactions",      id: "transactions"     },
      { icon: "ri-user-line",               label: "Users",             id: "users"            },
      { icon: "ri-shield-check-line",       label: "KYC Verification",  id: "kyc"              },
      { icon: "ri-megaphone-line",          label: "Marketing",         id: "campaign"         },
      { icon: "ri-customer-service-2-line", label: "Support Tickets",   id: "tickets"          },
      { icon: "ri-shield-line",             label: "Fraud Holds",       id: "fraud"            },
      { icon: "ri-wallet-3-line",           label: "Provider Balances", id: "provider_balances"},
    ],
  },
  {
    label: "Finance",
    items: [
      { icon: "ri-coin-line",     label: "Assets & Rates",   id: "assets"          },
      { icon: "ri-safe-line",     label: "Wallets",          id: "wallets"         },
      { icon: "ri-wifi-line",     label: "Data Plans",       id: "dataPlans"       },
      { icon: "ri-wifi-line",     label: "Set Providers",    id: "providers"       },
      { icon: "ri-coupon-line",   label: "Coupons",          id: "coupon"          },
      { icon: "ri-vip-crown-line",label: "Promo Codes",      id: "promo_codes"     },
      { icon: "ri-image-line",    label: "Marketing Slides", id: "marketing_slides"},
      { icon: "ri-store-2-line",  label: "Savings Catalog",  id: "catalog"         },
      { icon: "ri-building-line", label: "Companies",        id: "companies"       },
    ],
  },
  {
    label: "System",
    items: [
      { icon: "ri-alarm-warning-line",  label: "Alerts",            id: "alerts",           isAlert: true },
      { icon: "ri-notification-3-line", label: "Notifications",     id: "notifications"                   },
      { icon: "ri-toggle-line",         label: "Services",          id: "services_settings"               },
      { icon: "ri-slideshow-3-line",    label: "Onboarding Slides", id: "onboarding_slide"                },
      { icon: "ri-settings-4-line",     label: "App Settings",      id: "general_settings"                },
      { icon: "ri-safe-2-line",         label: "Savings Settings",  id: "savings_settings"                },
      { icon: "ri-gift-line",           label: "Reward Rules",      id: "reward_rules"                    },
      { icon: "ri-file-list-3-line",    label: "Audit Logs",        id: "audit_logs"                      },
      { icon: "ri-earth-line",           label: "Countries",         id: "countries"                        },
      { icon: "ri-settings-2-line",     label: "Config Settings",   id: "settings"                        },
      { icon: "ri-team-line",           label: "Admin Accounts",    id: "accounts"                        },
    ],
  },
];

export default function Sidebar({
  onNavChange,          // called AFTER navigation — used for mobile drawer close
  mobileOpen = false,
  onMobileClose,
  adminPermissions = [],
  alertUnread    = 0,
  alertCritical  = 0,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();

  // Derive active nav ID from the current URL path segment
  const currentSegment = location.pathname.replace(/^\//, "").split("/")[0] || "dashboard";
  // Reverse-look up which nav ID owns this path segment
  const activeNav = Object.entries(NAV_TO_PATH).find(
    ([, path]) => path.replace(/^\//, "") === currentSegment
  )?.[0] || currentSegment;

  const handleNavClick = (id) => {
    const perm = VIEW_PERMISSIONS[id];
    if (!hasPermission(adminPermissions, perm)) {
      navigate("/unauthorized");
      onMobileClose?.();
      return;
    }
    const path = NAV_TO_PATH[id] || `/${id}`;
    navigate(path);
    onNavChange?.(id);   // closes mobile drawer via AdminLayout
    onMobileClose?.();
  };

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const sidebarContent = (
    <aside className={`h-full bg-[#0f0f0f] border-r border-white/5 flex flex-col transition-all duration-300 shrink-0 ${collapsed ? "w-16" : "w-60"}`}>

      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-white/5">
        <div className="w-8 h-8 flex items-center justify-center bg-[#00C896] rounded-lg shrink-0">
          <i className="ri-exchange-funds-line text-black text-sm" />
        </div>
        {!collapsed && (
          <div className="ml-3 flex-1">
            <span className="font-bold text-white text-sm tracking-wide">Easwallet</span>
            <span className="block text-[10px] text-[#00C896] font-medium">Admin Console</span>
          </div>
        )}
        {onMobileClose && (
          <button onClick={onMobileClose}
            className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg text-white/40 hover:bg-white/5 lg:hidden cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-2 overflow-y-auto space-y-4">
        {NAV_GROUPS.map(group => {
          const visibleItems = group.items.filter(item =>
            hasPermission(adminPermissions, VIEW_PERMISSIONS[item.id])
          );
          if (!visibleItems.length) return null;

          return (
            <div key={group.label}>
              {!collapsed && (
                <p className="text-[10px] font-semibold text-white/25 uppercase tracking-widest px-3 mb-1.5">
                  {group.label}
                </p>
              )}
              <div className="space-y-0.5">
                {visibleItems.map(item => {
                  const isActive   = activeNav === item.id;
                  const alertBadge = item.isAlert && alertUnread > 0;

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item.id)}
                      className={`w-full flex items-center px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer relative ${
                        isActive
                          ? "bg-[#00C896]/15 text-[#00C896]"
                          : "text-white/40 hover:bg-white/5 hover:text-white/70"
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center shrink-0 relative">
                        <i className={`${item.icon} text-base`} />
                        {item.isAlert && alertCritical > 0 && collapsed && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
                        )}
                      </div>

                      {!collapsed && (
                        <>
                          <span className="ml-3 flex-1 text-left font-medium">{item.label}</span>
                          {alertBadge && (
                            <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              alertCritical > 0
                                ? "bg-red-500 text-white animate-pulse"
                                : "bg-amber-500/20 text-amber-400"
                            }`}>
                              {alertUnread > 99 ? "99+" : alertUnread}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="p-2 border-t border-white/5 hidden lg:block">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center px-3 py-2.5 rounded-lg text-white/30 hover:bg-white/5 hover:text-white/60 transition-colors cursor-pointer"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={collapsed ? "ri-arrow-right-s-line text-base" : "ri-arrow-left-s-line text-base"} />
          </div>
          {!collapsed && <span className="ml-3 text-sm">Collapse</span>}
        </button>
      </div>
    </aside>
  );

  return (
    <>
      <div className="hidden lg:flex h-screen">{sidebarContent}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={onMobileClose} />
          <div className="relative h-full w-64 flex flex-col">{sidebarContent}</div>
        </div>
      )}
    </>
  );
}